import { MenuGroup } from "@clockwise/design-system";
import { getListBoxClasses } from "@clockwise/design-system/src/constants/classes";
import { CwIdProps } from "@clockwise/design-system/src/types/cw-id";
import { CheckCircleFilled, InstantMix, SvgIconComponent } from "@clockwise/icons";
import { FlexRange } from "@clockwise/schema/v2";
import { Menu as HeadlessMenu, Portal, Transition } from "@headlessui/react";
import { SvgIcon } from "@material-ui/core";
import { DateRange, Event, ExpandMore } from "@material-ui/icons";
import classNames from "classnames";
import { isUndefined } from "lodash";
import React, { KeyboardEventHandler, PropsWithChildren, useRef, useState } from "react";
import { usePopper } from "react-popper";

const MENU_LABELS: Record<FlexRange, string> = {
  [FlexRange.Day]: "within the day",
  [FlexRange.Week]: "within the week",
  [FlexRange.SpecificDays]: "within specific workdays",
};

interface MenuProps extends CwIdProps {
  disabled?: boolean;
  fullWidth?: boolean;
  showExpandIcon?: boolean | "default";
  onOpen?: () => void;
  maxHeight?: CSSStyleDeclaration["maxHeight"];
  keepOpen?: boolean;
  handleFlexRangeChange: (flexRange: FlexRange) => void;
  openFlexSettings: () => void;
  flexRange: FlexRange;
}

type BaseProps = {
  onClick?: () => void;
  keepOpen?: boolean;
  disabled?: boolean;
  icon?: SvgIconComponent;
  iconProps?: { stroke?: string; fill?: string; className?: string; style?: { color?: string } };
};
type LinkProps = {
  href: string;
  /**
   @deprecated checked cannot be used with href prop
   */
  checked?: never;
} & BaseProps;

type NonLinkProps = {
  checked?: boolean;
  /**
   @deprecated href cannot be used with checked prop
   */
  href?: never;
} & BaseProps;

export const FlexEventMenuItem: React.FC<PropsWithChildren<NonLinkProps | LinkProps>> = (props) => {
  const { children, onClick, icon: SelectIcon, keepOpen, disabled = false, iconProps = {} } = props;

  const checkable = !isUndefined(props.checked);
  const CheckedIcon = props.checked ? CheckCircleFilled : SvgIcon;

  const hasLink = !isUndefined(props.href);

  const handleClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    // DevNote: https://github.com/tailwindlabs/headlessui/discussions/1122
    if (disabled) {
      return;
    }

    onClick?.();

    if (keepOpen) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <HeadlessMenu.Item disabled={disabled}>
      {({ active }) => (
        <div
          {...(checkable ? { "aria-checked": props.checked } : {})}
          className={getListBoxClasses(active, disabled)}
          onClick={handleClick}
        >
          <div className="cw-flex cw-items-center cw-gap-2">
            {hasLink ? (
              <a
                href={props.href}
                target="_blank"
                rel="noopener noreferrer"
                className="cw-no-underline cw-text-default cw-w-full"
              >
                {children}
              </a>
            ) : (
              <div className="cw-flex cw-flex-1 cw-justify-between cw-items-center">
                <div className="cw-flex cw-gap-2 cw-items-center">
                  {SelectIcon && (
                    <SelectIcon
                      {...iconProps}
                      className={classNames("cw-w-4 cw-h-4", iconProps?.className)}
                    />
                  )}
                  {children}
                </div>
                {checkable && <CheckedIcon className="cw-text-positive cw-ml-2 cw-text-sm" />}
              </div>
            )}
          </div>
        </div>
      )}
    </HeadlessMenu.Item>
  );
};

export const FlexEventMenu = ({
  disabled = false,
  onOpen,
  maxHeight = "288px",
  keepOpen = false,
  "cw-id": cwId,
  handleFlexRangeChange,
  openFlexSettings,
  flexRange,
}: MenuProps) => {
  const wasOpen = useRef(false);
  const menuItemsRef = useRef<HTMLDivElement | null>(null);
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const { styles, attributes, update: updatePopperInstance } = usePopper(trigger, container, {
    placement: "bottom-start",
    strategy: "fixed",
    modifiers: [
      {
        name: "flip",
        enabled: true,
      },
      { name: "offset", options: { offset: [0, 4] } },
    ],
  });

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    // DevNote: https://github.com/tailwindlabs/headlessui/discussions/1122
    if (!keepOpen) {
      return;
    }

    if (event.key === "Enter") {
      const activeItemId = menuItemsRef?.current?.getAttribute("aria-activedescendant");

      if (!activeItemId) {
        return;
      }

      const activeItem = document.getElementById(activeItemId);

      if (!activeItem) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      activeItem.click();
    }
  };

  return (
    <HeadlessMenu>
      {({ open }) => {
        if (open && !wasOpen.current) {
          wasOpen.current = true;
          setTimeout(() => {
            onOpen?.();
          }, 0);
        } else if (!open && wasOpen) {
          wasOpen.current = false;
        }
        return (
          <>
            <HeadlessMenu.Button
              cw-id={cwId}
              ref={setTrigger}
              disabled={disabled}
              className="cw-body-sm cw-text-11 cw-cursor-pointer cw-font-medium cw-border-none cw-rounded-full cw-pl-2 cw-pr-1 cw-h-5 cw-bg-neutral-inset pressed:cw-bg-neutral-inset-pressed hover:cw-bg-neutral-inset-pressed"
            >
              <div className="cw-block cw-truncate cw-w-full">
                <div className="cw-flex cw-items-center cw-justify-between cw-gap-0.5">
                  <div className="cw-flex cw-items-center cw-gap-1">{MENU_LABELS[flexRange]}</div>
                  <ExpandMore className="cw-h-[14px] cw-w-[14px]" aria-hidden="true" />
                </div>
              </div>
            </HeadlessMenu.Button>
            <Portal>
              <div
                ref={setContainer}
                style={{
                  ...styles.popper,
                  minWidth: trigger?.scrollWidth,
                  zIndex: 10000,
                }}
                {...attributes.popper}
              >
                <Transition
                  leave="cw-transition cw-ease-in cw-duration-100"
                  leaveFrom="cw-opacity-100"
                  leaveTo="cw-opacity-0"
                  enter="cw-transition cw-ease-in cw-duration-100"
                  enterFrom="cw-opacity-0"
                  enterTo="cw-opacity-100"
                >
                  <HeadlessMenu.Items
                    className={classNames(
                      "cw-z-50 cw-min-w-full cw-p-1 cw-overflow-auto",
                      "cw-font-body cw-font-normal cw-text-13 cw-bg-default cw-rounded-lg cw-shadow-selectPopup cw-min-w-[180px]",
                    )}
                    style={{ maxHeight }}
                    ref={(element) => {
                      // Update popper positioning after the content is rendered
                      void updatePopperInstance?.();
                      menuItemsRef.current = element;
                    }}
                    onKeyDown={handleKeyDown}
                  >
                    <MenuGroup>
                      <FlexEventMenuItem
                        onClick={() => {
                          handleFlexRangeChange(FlexRange.Day);
                        }}
                        icon={Event}
                        iconProps={{ className: "cw-text-muted" }}
                        checked={flexRange === FlexRange.Day}
                      >
                        within the day
                      </FlexEventMenuItem>
                      <FlexEventMenuItem
                        onClick={() => {
                          handleFlexRangeChange(FlexRange.Week);
                        }}
                        icon={DateRange}
                        iconProps={{ className: "cw-text-muted" }}
                        checked={flexRange === FlexRange.Week}
                      >
                        within the week
                      </FlexEventMenuItem>
                    </MenuGroup>
                    <MenuGroup>
                      <FlexEventMenuItem
                        onClick={openFlexSettings}
                        icon={InstantMix}
                        iconProps={{ className: "cw-text-muted" }}
                      >
                        Custom
                      </FlexEventMenuItem>
                    </MenuGroup>
                  </HeadlessMenu.Items>
                </Transition>
              </div>
            </Portal>
          </>
        );
      }}
    </HeadlessMenu>
  );
};
